/**
 * Resolves a session-reminder push notification into an in-app route.
 *
 * The reminder scheduler (`infrastructure/lambda/scan-due-reminders`) sends FCM
 * messages with a `data` payload whose `type` is one of the values below and
 * which carries the session `uuid`. Tapping the notification should deep-link to
 * the relevant session.
 *
 * WIRING (follow-up — intentionally NOT done here to avoid touching the shared
 * push handler): in `frontend/src/services/push-registration.ts`, inside the
 * existing `pushNotificationActionPerformed` listener, add:
 *
 *   const route = resolveSessionReminderRoute(data)
 *   if (route) { navigationHandler?.(route); return }
 *
 * That is the only change needed to activate session-reminder deep-links.
 */

import { SESSION_CHECK_IN_NOTIFICATION_ACTION } from '@shared/sessions/check-in-notification'

export type SessionReminderType =
  | 'SESSION_REMINDER_START'
  | 'SESSION_REMINDER_END'
  | 'SESSION_PREP'
  | 'SESSION_LEAVE_NOW'
  | 'SESSION_PREP_TRAVEL'
  | 'SESSION_ARRIVED_CHECK_IN'
  | 'PRE_SHIFT_MEMBER_TRAVEL_ETA'
  | 'PROVIDER_EN_ROUTE'
  /** Legacy (pre-2026-10-08 backend): per-stage "Halfway"/"Nearly there" pushes. */
  | 'PROVIDER_JOURNEY_STAGE'
  /** Legacy (pre-2026-10-08 backend): 500 m "very close by" push. */
  | 'PROVIDER_VERY_CLOSE'
  /** The single "less than 5 min away" push. */
  | 'PROVIDER_NEARBY'
  | 'PROVIDER_ARRIVED'
  | 'SESSION_CHECKED_OUT'
  /** Telehealth (phone / video) member / follower pushes. */
  | 'SESSION_STARTED'
  | 'SESSION_ENDED'
  | 'SESSION_CANCEL_REQUEST'
  | 'SESSION_CANCELLED'
  | 'SESSION_NOT_STARTED'
  | 'CALLBACK_SESSION_BOOKED'

/** The subset of the FCM `data` payload this handler cares about. */
export interface SessionReminderData {
  type?: string
  uuid?: string
  /** Some senders use `sessionUuid`; accept either for resilience. */
  sessionUuid?: string
  panel?: string
  /** `check-in` when the push carries the one-tap "Check in" action. */
  notificationAction?: string
  /** Data-only Android copies the alert into these keys (see fcm.ts). */
  title?: string
  body?: string
}

/** Every session-event `data.type` the backend currently sends. */
export const SESSION_REMINDER_TYPES: readonly SessionReminderType[] = [
  'SESSION_REMINDER_START',
  'SESSION_REMINDER_END',
  'SESSION_PREP',
  'SESSION_LEAVE_NOW',
  'SESSION_PREP_TRAVEL',
  'SESSION_ARRIVED_CHECK_IN',
  'PRE_SHIFT_MEMBER_TRAVEL_ETA',
  'PROVIDER_EN_ROUTE',
  'PROVIDER_JOURNEY_STAGE',
  'PROVIDER_VERY_CLOSE',
  'PROVIDER_NEARBY',
  'PROVIDER_ARRIVED',
  'SESSION_CHECKED_OUT',
  'SESSION_STARTED',
  'SESSION_ENDED',
  'SESSION_CANCEL_REQUEST',
  'SESSION_CANCELLED',
  'SESSION_NOT_STARTED',
  'CALLBACK_SESSION_BOOKED',
]

const REMINDER_TYPES: ReadonlySet<string> = new Set<SessionReminderType>(SESSION_REMINDER_TYPES)

export function isSessionReminder(data: SessionReminderData): boolean {
  return typeof data.type === 'string' && REMINDER_TYPES.has(data.type)
}

/**
 * Returns the deep-link route for a session-reminder payload, or `null` when the
 * payload is not a session reminder (so the caller can fall through to other
 * notification types). The route opens the Sessions screen focused on the
 * session via the `session` query param.
 */
export function resolveSessionReminderRoute(data: SessionReminderData): string | null {
  if (!isSessionReminder(data)) return null
  const uuid = (data.uuid ?? data.sessionUuid ?? '').trim()
  const panel =
    data.panel ??
    (data.type === 'SESSION_PREP' || data.type === 'SESSION_LEAVE_NOW'
      ? 'prep'
      : data.type === 'PROVIDER_EN_ROUTE'
        ? 'enroute'
        : null)
  const params = new URLSearchParams()
  if (uuid) params.set('session', uuid)
  if (panel) params.set('panel', panel)
  const query = params.toString()
  return query ? `/sessions?${query}` : '/sessions'
}

/**
 * Route for the notification's one-tap "Check in" ACTION (vs a plain body
 * tap, which only opens the session). Returns `null` unless the event really
 * is the check-in action:
 *
 *  - iOS: Capacitor reports the category action as `actionId === 'check-in'`;
 *  - Android: the native action PendingIntent carries
 *    `data.notificationAction === 'check-in'` (the body-tap intent explicitly
 *    strips that key — see SessionNotificationHelper.java), and Capacitor
 *    reports it as a plain `tap`.
 *
 * The `action=check-in` param makes the session detail perform the check-in
 * immediately on load — no further taps — then show the undo snackbar.
 */
export function resolveSessionCheckInActionRoute(
  data: SessionReminderData,
  actionId?: string,
): string | null {
  const isAction =
    actionId === SESSION_CHECK_IN_NOTIFICATION_ACTION ||
    data.notificationAction === SESSION_CHECK_IN_NOTIFICATION_ACTION
  if (!isAction) return null
  const uuid = (data.uuid ?? data.sessionUuid ?? '').trim()
  if (!uuid) return null
  const params = new URLSearchParams()
  params.set('session', uuid)
  params.set('action', SESSION_CHECK_IN_NOTIFICATION_ACTION)
  return `/sessions?${params.toString()}`
}
